import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Copy, Check, Shield, Settings, ArrowRight, AlertTriangle, Sliders } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

export default function DocsGuardrails() {
  const [copiedCode, setCopiedCode] = useState(null);

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const guardrails = [
    {
      name: 'approval_mode',
      type: 'enum',
      options: 'auto, approve, notify, disabled',
      default: 'approve',
      description: 'Controls how agent actions are handled. "auto" lets the trust tier decide, "approve" requires manual approval, "notify" auto-executes but notifies you, "disabled" turns the agent off.',
    },
    {
      name: 'confidence_threshold',
      type: 'float',
      options: '0.0 - 1.0',
      default: '0.7',
      description: 'Minimum confidence score required for an agent to act. Actions below this threshold are escalated for review.',
    },
    {
      name: 'max_actions_per_hour',
      type: 'integer',
      options: '1 - 1000',
      default: '50',
      description: 'Rate limit on how many actions an agent can execute per hour. Prevents runaway automation.',
    },
    {
      name: 'max_financial_impact',
      type: 'USD',
      options: '$1 - $100,000',
      default: '$1,000',
      description: 'Maximum financial impact of a single agent action. Actions exceeding this are held for approval regardless of trust tier.',
    },
    {
      name: 'allowed_action_types',
      type: 'array',
      options: 'string[]',
      default: '[] (all allowed)',
      description: 'Whitelist of action types the agent can perform. Empty means all actions are allowed.',
    },
    {
      name: 'blocked_action_types',
      type: 'array',
      options: 'string[]',
      default: '[] (none blocked)',
      description: 'Blacklist of action types the agent cannot perform. Takes priority over allowed_action_types.',
    },
    {
      name: 'custom_rules',
      type: 'JSON',
      options: 'object',
      default: '{}',
      description: 'Advanced rules engine for complex conditions. Supports conditional logic based on product category, time of day, inventory levels, etc.',
    },
  ];

  const financialLimits = [
    { tier: 'Probation', autoApprove: '$0', forceApproval: '$0', maxFinancial: 'All actions need approval' },
    { tier: 'Supervised', autoApprove: '$50', forceApproval: '$200', maxFinancial: 'Up to $200 with auto-approve threshold' },
    { tier: 'Trusted', autoApprove: '$500', forceApproval: '$2,000', maxFinancial: 'Up to $2,000 with auto-approve threshold' },
    { tier: 'Autonomous', autoApprove: '$5,000', forceApproval: '$10,000', maxFinancial: 'Up to $10,000 with auto-approve threshold' },
  ];

  const dashboardExample = `// Configure guardrails via the dashboard:
// Navigate to: Settings → Agents → Select Agent → Guardrails
//
// The dashboard provides a visual editor for all guardrail
// settings with real-time validation and preview.`;

  const apiExample = `// Configure guardrails via API
const response = await fetch('/api/v1/agents/pricing_agent/guardrails', {
  method: 'PUT',
  headers: {
    'Content-Type': 'application/json',
    'X-CI-Api-Key': 'your_api_key',
    'X-CI-Merchant-Id': 'merch_xyz789'
  },
  body: JSON.stringify({
    approval_mode: 'auto',
    confidence_threshold: 0.8,
    max_actions_per_hour: 30,
    max_financial_impact: 500,
    allowed_action_types: ['price_adjustment', 'deal_creation'],
    blocked_action_types: ['product_delete'],
    custom_rules: {
      max_discount_pct: 25,
      min_margin_pct: 10,
      blackout_hours: [0, 1, 2, 3, 4, 5]
    }
  })
});`;

  const messagingExample = `// Configure guardrails via messaging (/policy command)
/policy set pricing_agent.approval_mode auto
// ✅ pricing_agent approval_mode set to auto

/policy set pricing_agent.confidence_threshold 0.85
// ✅ pricing_agent confidence_threshold set to 0.85

/policy set pricing_agent.max_actions_per_hour 25
// ✅ pricing_agent max_actions_per_hour set to 25

/policy
// 📋 Current policies:
// pricing_agent:
//   approval_mode: auto
//   confidence_threshold: 0.85
//   max_actions_per_hour: 25
//   max_financial_impact: $500
//   trust_tier: supervised`;

  const conservativeExample = `{
  "approval_mode": "approve",
  "confidence_threshold": 0.9,
  "max_actions_per_hour": 10,
  "max_financial_impact": 100,
  "allowed_action_types": ["price_adjustment"],
  "blocked_action_types": ["product_delete", "bulk_update"],
  "custom_rules": {
    "max_discount_pct": 10,
    "require_approval_weekends": true
  }
}`;

  const balancedExample = `{
  "approval_mode": "auto",
  "confidence_threshold": 0.7,
  "max_actions_per_hour": 50,
  "max_financial_impact": 1000,
  "allowed_action_types": [],
  "blocked_action_types": ["product_delete"],
  "custom_rules": {
    "max_discount_pct": 25,
    "min_margin_pct": 10
  }
}`;

  const aggressiveExample = `{
  "approval_mode": "notify",
  "confidence_threshold": 0.5,
  "max_actions_per_hour": 200,
  "max_financial_impact": 5000,
  "allowed_action_types": [],
  "blocked_action_types": [],
  "custom_rules": {
    "max_discount_pct": 50,
    "allow_flash_deals": true
  }
}`;

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <Badge className="bg-[#3EF1C7]/20 text-[#3EF1C7] border-[#3EF1C7]/30 mb-4">
          AI Agents
        </Badge>
        <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Guardrails
        </h1>
        <p className="text-lg text-[#94A3B8]">
          Guardrails are safety limits that control what AI agents can and cannot do.
          Configure them globally or per-agent to match your risk tolerance.
        </p>
      </div>

      {/* Available Guardrails */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
          <Shield className="w-6 h-6 text-[#3EF1C7]" />
          Available Guardrails
        </h2>
        <div className="space-y-4">
          {guardrails.map((g, i) => (
            <Card key={i} className="bg-[#0D1628] border-[#22304A]">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-2">
                  <code className="text-[#3EF1C7] font-semibold text-sm">{g.name}</code>
                  <div className="flex gap-2">
                    <Badge className="bg-[#22304A] text-[#94A3B8] border-[#22304A] text-xs">{g.type}</Badge>
                    <Badge className="bg-[#22304A] text-[#94A3B8] border-[#22304A] text-xs">default: {g.default}</Badge>
                  </div>
                </div>
                <p className="text-[#E2E8F0] text-sm mb-1">{g.description}</p>
                <p className="text-[#94A3B8] text-xs">Options: {g.options}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Configuration Methods */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
          <Settings className="w-6 h-6 text-[#3EF1C7]" />
          Configuration Methods
        </h2>

        <h3 className="text-lg font-semibold text-[#E2E8F0] mb-3">Via Dashboard</h3>
        <div className="relative mb-6">
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{dashboardExample}</code>
          </pre>
        </div>

        <h3 className="text-lg font-semibold text-[#E2E8F0] mb-3">Via API</h3>
        <div className="relative mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 text-[#94A3B8] hover:text-white"
            onClick={() => copyToClipboard(apiExample, 'api')}
          >
            {copiedCode === 'api' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{apiExample}</code>
          </pre>
        </div>

        <h3 className="text-lg font-semibold text-[#E2E8F0] mb-3">Via Messaging (/policy command)</h3>
        <div className="relative mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 text-[#94A3B8] hover:text-white"
            onClick={() => copyToClipboard(messagingExample, 'messaging')}
          >
            {copiedCode === 'messaging' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{messagingExample}</code>
          </pre>
        </div>
      </section>

      {/* Per-Agent vs Global */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
          <Sliders className="w-6 h-6 text-[#3EF1C7]" />
          Per-Agent vs Global Guardrails
        </h2>
        <p className="text-[#94A3B8] mb-4">
          Guardrails can be set at two levels:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader>
              <CardTitle className="text-[#E2E8F0] text-lg">Global Guardrails</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#94A3B8] text-sm">
                Apply to all agents by default. Set these as your baseline safety net.
                Configured in <strong className="text-[#E2E8F0]">Settings → Agent Policies → Global</strong>.
              </p>
            </CardContent>
          </Card>
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader>
              <CardTitle className="text-[#E2E8F0] text-lg">Per-Agent Guardrails</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#94A3B8] text-sm">
                Override global settings for a specific agent. Per-agent settings always take precedence.
                The more restrictive value wins when comparing global vs per-agent.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Trust Tier Interaction */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
          <AlertTriangle className="w-6 h-6 text-[#3EF1C7]" />
          Financial Limits by Trust Tier
        </h2>
        <p className="text-[#94A3B8] mb-4">
          Guardrails work in conjunction with trust tiers. The trust tier sets baseline financial limits,
          and guardrails can further restrict (but not expand) those limits.
        </p>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#22304A]">
                  <th className="text-left p-3 text-[#94A3B8] font-medium">Tier</th>
                  <th className="text-left p-3 text-[#94A3B8] font-medium">Auto-Approve Below</th>
                  <th className="text-left p-3 text-[#94A3B8] font-medium">Force Approval Above</th>
                  <th className="text-left p-3 text-[#94A3B8] font-medium">Behavior</th>
                </tr>
              </thead>
              <tbody>
                {financialLimits.map((row, i) => (
                  <tr key={i} className="border-b border-[#22304A] last:border-0">
                    <td className="p-3 text-[#E2E8F0] font-medium">{row.tier}</td>
                    <td className="p-3 text-[#3EF1C7]">{row.autoApprove}</td>
                    <td className="p-3 text-[#EF4444]">{row.forceApproval}</td>
                    <td className="p-3 text-[#94A3B8]">{row.maxFinancial}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </section>

      {/* Example Configurations */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4">
          Example Configurations
        </h2>

        <h3 className="text-lg font-semibold text-[#E2E8F0] mb-3">Conservative (Low Risk)</h3>
        <p className="text-[#94A3B8] mb-3 text-sm">Best for new merchants or high-value products. Maximum human oversight.</p>
        <div className="relative mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 text-[#94A3B8] hover:text-white"
            onClick={() => copyToClipboard(conservativeExample, 'conservative')}
          >
            {copiedCode === 'conservative' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{conservativeExample}</code>
          </pre>
        </div>

        <h3 className="text-lg font-semibold text-[#E2E8F0] mb-3">Balanced (Recommended)</h3>
        <p className="text-[#94A3B8] mb-3 text-sm">Good default for most merchants. Trust-tier-driven automation with sensible limits.</p>
        <div className="relative mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 text-[#94A3B8] hover:text-white"
            onClick={() => copyToClipboard(balancedExample, 'balanced')}
          >
            {copiedCode === 'balanced' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{balancedExample}</code>
          </pre>
        </div>

        <h3 className="text-lg font-semibold text-[#E2E8F0] mb-3">Aggressive (High Autonomy)</h3>
        <p className="text-[#94A3B8] mb-3 text-sm">For experienced merchants comfortable with AI-driven decisions. Maximize speed and volume.</p>
        <div className="relative mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 text-[#94A3B8] hover:text-white"
            onClick={() => copyToClipboard(aggressiveExample, 'aggressive')}
          >
            {copiedCode === 'aggressive' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{aggressiveExample}</code>
          </pre>
        </div>
      </section>

      {/* What's Next */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">What's Next?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link to="/docs/trust-ladder">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/50 transition-colors cursor-pointer">
              <CardContent className="pt-6">
                <h3 className="text-[#E2E8F0] font-semibold mb-2 flex items-center gap-2">
                  Trust Ladder <ArrowRight className="w-4 h-4 text-[#3EF1C7]" />
                </h3>
                <p className="text-[#94A3B8] text-sm">Understand how agents earn autonomy through the trust tier system.</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/docs/agent-admin">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/50 transition-colors cursor-pointer">
              <CardContent className="pt-6">
                <h3 className="text-[#E2E8F0] font-semibold mb-2 flex items-center gap-2">
                  Agent Admin <ArrowRight className="w-4 h-4 text-[#3EF1C7]" />
                </h3>
                <p className="text-[#94A3B8] text-sm">Manage agents and policies through messaging commands.</p>
              </CardContent>
            </Card>
          </Link>
        </div>
      </section>
    </div>
  );
}
